import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useConfig } from '../contexts/ConfigContext';
import LoadingAnimation from './LoadingAnimation';
import Footer from './Footer';
import EventCard from './EventCard';

const TOTUS_TUUS_PRAYER = `Totally yours,
Immaculate Conception, Mary my Mother,
Live in me, Act in me,
Speak in me and through me,
Think your thoughts in my mind,
Love through my heart,
Give me your dispositions and feelings,
Teach, lead me and guide me to Jesus,
Correct, enlighten and expand my thoughts and behavior,
Possess my soul,
Take over my entire personality and life, replace it with Yourself,
Incline me to constant adoration,
Pray in me and through me,
Let me live in you and keep me in this union always.`;

const Homepage = () => {
  const config = useConfig();

  const [events, setEvents] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSlide, setActiveSlide] = useState(0);
  const [loadedSlides, setLoadedSlides] = useState(new Set());
  const imgRefs = useRef({});
  const touchStartX = useRef(null);

  // Mark any images already in the browser cache as loaded
  useEffect(() => {
    if (!gallery.length) return;
    setLoadedSlides(prev => {
      const next = new Set(prev);
      gallery.forEach((_, i) => {
        const el = imgRefs.current[i];
        if (el && el.complete) next.add(i);
      });
      return next;
    });
  }, [gallery]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsSnap, gallerySnap] = await Promise.all([
          getDocs(query(collection(db, 'events'), orderBy('date', 'asc'))),
          getDocs(query(collection(db, 'gallery'), orderBy('order', 'asc')))
        ]);

        const now = new Date();
        const upcomingEvents = eventsSnap.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(e => {
            const status = e.status || 'published';
            if (status === 'deleted' || status === 'draft') return false;
            if (status === 'scheduled' && e.scheduled_at && e.scheduled_at.toDate() > now) return false;
            return e.date && e.date.toDate() >= now;
          });

        setEvents(upcomingEvents);
        setGallery(gallerySnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.error('Error fetching homepage data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <div className="stt-page"><LoadingAnimation /></div>;

  return (
    <div className="stt-page">
      {/* Hero */}
      <div className="stt-hero">
        <h1 className="stt-hero-title">Southie Totus Tuus</h1>
        <p className="stt-hero-subtitle">A Gate of Heaven and St. Brigid Young Adults Community</p>
      </div>

      <div className="stt-content">

        {/* Faith & Fellowship */}
        <div className="stt-section">
          <h2 className="stt-section-title">Faith &amp; Fellowship</h2>
          {config.scheduleText ? (
            <p className="stt-section-body" style={{ textAlign: 'center' }}>{config.scheduleText}</p>
          ) : (
            <>
              <p className="stt-section-body" style={{ textAlign: 'center' }}>
                We invite you to join us Wednesday evenings for our Holy Hour Happy Hour at <a href="https://www.gateofheavenstbrigid.org/" target="_blank" rel="noopener noreferrer">Gate of Heaven Church</a> in South Boston!
              </p>
              <div className="stt-section-body stt-schedule" style={{ marginTop: '16px' }}>
                <div className="stt-schedule-item">
                  <p><strong>7:00 PM</strong> — Adoration &amp; Confessions Begins</p>
                  <p><em>Exposition of the Blessed Sacrament featuring live worship music</em></p>
                </div>
                <div className="stt-schedule-item">
                  <p><strong>7:45 PM</strong> — Catechism Conversations</p>
                  <p><em>Previous topics include the moral life, the mass, and the sacraments</em></p>
                </div>
                <div className="stt-schedule-item">
                  <p><strong>8:30 PM</strong> — Happy Hour</p>
                  <p><em>Finish the night with fellowship at The Playwright!</em></p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Our Mission */}
        {config.missionText && (
          <div className="stt-section">
            <h2 className="stt-section-title">Our Mission</h2>
            <p className="stt-section-body" style={{ textAlign: 'center' }}>{config.missionText}</p>
          </div>
        )}

        {/* Upcoming Events */}
        <div className="stt-section">
          <h2 className="stt-section-title">Upcoming Events</h2>
          {events.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#888' }}>No upcoming events at this time. Check back soon!</p>
          ) : (
            events.slice(0, 3).map(event => <EventCard key={event.id} event={event} />)
          )}
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <Link to="/events" className="stt-see-all-link">See All Events</Link>
          </div>
        </div>

        {/* Gallery carousel */}
        {gallery.length > 0 && (
          <div className="stt-section">
            <div className="stt-carousel">
              <div
                className="stt-carousel-viewport"
                onTouchStart={e => { touchStartX.current = e.touches[0].clientX; }}
                onTouchEnd={e => {
                  if (touchStartX.current === null) return;
                  const dx = e.changedTouches[0].clientX - touchStartX.current;
                  touchStartX.current = null;
                  if (Math.abs(dx) < 40) return;
                  if (dx < 0) setActiveSlide(i => (i + 1) % gallery.length);
                  else setActiveSlide(i => (i - 1 + gallery.length) % gallery.length);
                }}
              >
                {gallery.length > 1 && (
                  <button
                    className="stt-carousel-arrow stt-carousel-prev"
                    onClick={() => setActiveSlide(i => (i - 1 + gallery.length) % gallery.length)}
                    aria-label="Previous"
                  >
                    &#8249;
                  </button>
                )}
                <div className="stt-carousel-slide">
                  {!loadedSlides.has(activeSlide) && (
                    <div className="stt-carousel-placeholder">
                      <div className="stt-spinner" />
                    </div>
                  )}
                  {gallery.map((photo, i) => (
                    <img
                      key={photo.id}
                      ref={el => { imgRefs.current[i] = el; }}
                      src={photo.url}
                      alt={photo.caption || ''}
                      onLoad={() => setLoadedSlides(prev => new Set(prev).add(i))}
                      style={{ display: i === activeSlide && loadedSlides.has(i) ? 'block' : 'none' }}
                    />
                  ))}
                  <p className="stt-carousel-caption">
                    {loadedSlides.has(activeSlide) ? (gallery[activeSlide].caption || '') : ''}
                  </p>
                </div>
                {gallery.length > 1 && (
                  <button
                    className="stt-carousel-arrow stt-carousel-next"
                    onClick={() => setActiveSlide(i => (i + 1) % gallery.length)}
                    aria-label="Next"
                  >
                    &#8250;
                  </button>
                )}
              </div>
              {gallery.length > 1 && (
                <div className="stt-carousel-dots">
                  {gallery.map((_, i) => (
                    <button
                      key={i}
                      className={`stt-carousel-dot${i === activeSlide ? ' active' : ''}`}
                      onClick={() => setActiveSlide(i)}
                      aria-label={`Photo ${i + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

      </div>

      <div className="stt-content">

        {/* Totus Tuus Prayer */}
        <div className="stt-section">
          <h2 className="stt-section-title">Totus Tuus Prayer</h2>
          <div className="stt-prayer-block">
            <p className="stt-prayer-attribution">
              Written by Saint John Paul II to Mary Immaculate
            </p>
            <p className="stt-prayer-text">{TOTUS_TUUS_PRAYER}</p>
          </div>
        </div>

        {/* Contact */}
        <div className="stt-section" style={{ textAlign: 'center' }}>
          <h2 className="stt-section-title">Contact</h2>
          <p className="stt-section-body" style={{ marginBottom: '12px' }}>
            Please reach out to our Southie Totus Tuus Leadership Team:
          </p>
          <a href={`mailto:${config.contactEmail}`} className="stt-contact-email">
            {config.contactEmail}
          </a>
        </div>

      </div>

      <Footer />
    </div>
  );
};

export default Homepage;
